/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance"; // ✅ con token automático

import CustomerForm from "../Components/CustomerForm";
import PmsConfirmDialog from "../Components/PmsConfirmDialog";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  normalizeCurrencyCode,
} from "../currency/currencyPrecision";
import { formatReservationBaseAmount } from "../currency/reservationCurrency";
import { submitWaivedTerminalWorkflow } from "../reservations/terminalReservationWorkflow";
import {
  fixedScaleAmountToCents,
  prepareCheckInManualPayment,
} from "./checkInPaymentCurrency";
import "../CheckIn.css";

function moneyShapeToCents(value) {
  if (value && typeof value === "object" && Number.isFinite(Number(value.cents))) {
    return Number(value.cents);
  }
  if (value && typeof value === "object" && value.amount != null) {
    return fixedScaleAmountToCents(value.amount) || 0;
  }
  return fixedScaleAmountToCents(value) || 0;
}

function normalizeManualPaymentMethod(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "credit card" || key === "direct card" || key === "manual_card") return "manual_card";
  if (key === "bank transfer" || key === "bank_transfer") return "bank_transfer";
  return "cash";
}

function findReservationBalance(paymentBalance, reservationId) {
  const rows = Array.isArray(paymentBalance?.reservations) ? paymentBalance.reservations : [];
  return rows.find((row) => Number(row.reservation_id) === Number(reservationId)) || null;
}

async function fetchPaymentBalanceForReservation(reservationData) {
  const groupId = reservationData?.reservation_group_id;
  if (!groupId) return null;

  const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservation-groups/${groupId}/payment-balance`);
  return res.data || null;
}

function CheckIn() {
    const { id } = useParams();
    const navigate = useNavigate();
    const {
      configuration: currencyConfiguration,
      displayCurrency,
      convertFromBase,
      currencyCatalog,
    } = useCurrencyDisplay();
    const [reservation, setReservation] = useState(null);
    const [paymentBalance, setPaymentBalance] = useState(null);
    const [guests, setGuests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState({});
    const [editingRoom, setEditingRoom] = useState(false);
    const [availableRooms, setAvailableRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [editingPayment, setEditingPayment] = useState(false);
    const [newPayment, setNewPayment] = useState({
      amount_paid: "",
      payment_method: "Cash"});
    const [minAgeChild, setMinAgeChild] = useState(null);
    const [maxAgeChild, setMaxAgeChild] = useState(null);
    const [statusActionLoading, setStatusActionLoading] = useState("");
    const [showNoShowDialog, setShowNoShowDialog] = useState(false);
      
    const handleAgeValidation = (index, birthDate) => {
      console.log("📞 Validación recibida desde CustomerForm:", { index, birthDate });
    
      const guest = guests?.[index];
      if (!guest || !guest.customer) {
        console.warn("❌ guest o guest.customer está undefined", index, guest);
        return;
      }
    
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

    
      console.log(`👤 Guest ${index}: Edad calculada = ${age}`);
    
      const isChild = index >= reservation.adults;
      const isAdult = !isChild;
    
      if (isChild && (age < minAgeChild || age > maxAgeChild)) {
        alert(`The age for a child should be between ${minAgeChild} and ${maxAgeChild}. Current age: ${age}`);
        const updatedGuests = [...guests];
        updatedGuests[index].customer.birth_date = "";
        setGuests(updatedGuests);
        return;
      }
    
      if (isAdult && age <= maxAgeChild) {
        alert(`The customer is too young to be considered an adult. Current age: ${age}`);
        const updatedGuests = [...guests];
        updatedGuests[index].customer.birth_date = "";
        setGuests(updatedGuests);
        return;
      }
    
      console.log("✅ Edad válida");
    };
  
    
    
    useEffect(() => {
      const fetchAgeSettings = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: {
              business_unit_id: reservation?.business_unit_id || 1,
            }
          });

          console.log("Configuraciones recibidas:", response.data);
    
          // Aquí asegúrate de que minAgeChild y maxAgeChild se obtienen correctamente
          setMinAgeChild(response.data.max_age_baby + 1);
          setMaxAgeChild(response.data.max_age_child);

    
          console.log("❓ Response from API:", response.data);
        } catch (error) {
          console.error("❌ Error al cargar edades permitidas desde hotel_settings:", error);
        }
      };
    
      fetchAgeSettings();
    }, [reservation?.business_unit_id]);
    
    

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`)
            .then(response => {
                setReservation(response.data);
                fetchPaymentBalanceForReservation(response.data)
                  .then((balance) => setPaymentBalance(balance))
                  .catch((error) => {
                    console.warn("Payment balance unavailable:", error?.response?.data || error?.message || error);
                    setPaymentBalance(null);
                  });
                let currentGuests = response.data.guests || [];

                // Verificar si los datos de los huéspedes existen
                if (currentGuests.length === 0) {
                  console.error("No guests found for this reservation");
                }
                
                // Agregar espacios vacíos para los acompañantes si faltan huéspedes
                const expectedGuests = (response.data.adults || 1) + (response.data.children || 0);
                  while (currentGuests.length < expectedGuests) {
                    currentGuests.push({
                      customer: {
                        first_name: "",
                        last_name: "",
                        document_type: "",
                        document_number: "",
                        middle_name: "",
                        second_last_name: "",
                        email: "",
                        phone: "",
                        address: "",
                        postal_code: "",
                        country: "",
                        city: "",
                        gender: "",
                        birth_date: ""
                      },
                      is_main_guest: false,
                      relationship: ""
                    });
                  }

    
                setGuests(currentGuests);
                console.log("Datos de los huéspedes:", currentGuests);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching reservation:", error);
                setLoading(false);
            });
    }, [id]);
    
    useEffect(() => {
      document.body.classList.add("check-in-scroll");
  
      return () => {
          document.body.classList.remove("check-in-scroll");
      };
  }, []);

  useEffect(() => {
    // Vincular función de validación al objeto global
    window.handleExternalAgeValidation = handleAgeValidation;
  
    return () => {
      delete window.handleExternalAgeValidation;
    };
  }, [guests, reservation, minAgeChild, maxAgeChild]);
  
  
  const fetchAvailableRooms = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/available`, {
        params: {
          business_unit_id: reservation.business_unit_id,
          room_category_id: reservation.room_category_id,
          check_in_date: reservation.check_in_date,
          check_out_date: reservation.check_out_date
        }
      });
      setAvailableRooms(response.data);
    } catch (error) {
      console.error("Error fetching available rooms:", error);
    }
  };
  

  const handleAssignRoom = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/reservations/${reservation.id}`, {
        room_id: selectedRoomId
      });
  
      // Refrescar reserva
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${reservation.id}`);
      setReservation(updated.data);
      setEditingRoom(false);
    } catch (error) {
      console.error("Error updating room:", error);
    }
  };
  

    const handleEdit = (index, field) => {
        setEditing({ ...editing, [`${index}-${field}`]: true });
    };

    const handleConfirm = async (index, field) => {
        const updatedGuests = [...guests];
        const updatedValue = updatedGuests[index].customer[field]; 
        const customerId = updatedGuests[index].customer.id;
    
        console.log("Enviando actualización:", {
            id: customerId,
            [field]: updatedValue
        });
    
        setGuests(updatedGuests);
        setEditing({ ...editing, [`${index}-${field}`]: false });
    
        // Enviar actualización al backend
        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customerId}`, {
                [field]: updatedValue
            });
            console.log("Campo actualizado con éxito");
        } catch (error) {
            console.error("Error al actualizar el campo:", error);
            alert("Hubo un problema al actualizar el dato.");
        }
    };
    
    

    const handleCheckIn = async () => {
      if (!(reservation.effective_room_id || reservation.room_id)) {
        console.warn("❌ Check-in bloqueado: no hay habitación asignada.");
        alert("You must assign a room before checking in.");
        return;
      }
    
        // Verificar que todos los huéspedes tengan datos completos
        const allGuestsComplete = guests.every(guest => {
          const c = guest.customer;
        
          return (
            c &&
            c.first_name &&
            c.last_name &&
            c.document_type &&
            c.document_number &&
            c.email &&
            c.phone &&
            c.gender &&
            c.birth_date &&
            c.country &&
            c.city &&
            c.address &&
            c.postal_code &&
            (guest.is_main_guest || guest.relationship)
          );
        });
        
        
    
        if (guests.length < reservation.adults) {
            alert(`The reservation specifies ${reservation.adults} adults, but only ${guests.length} guests are registered.`);
            return;
        }
    
        if (!allGuestsComplete) {
          const firstIncompleteIndex = guests.findIndex((guest) => {
            const c = guest.customer;
            return !(
              c &&
              c.first_name &&
              c.last_name &&
              c.document_type &&
              c.document_number &&
              c.email &&
              c.phone &&
              c.gender &&
              c.birth_date &&
              c.country &&
              c.city &&
              c.address &&
              c.postal_code &&
              (guest.is_main_guest || guest.relationship)
            );
          });
        
      
          const firstIncomplete = guests[firstIncompleteIndex];
          const c = firstIncomplete.customer;
          const missingFields = [];
      
          if (!c.first_name) missingFields.push("First Name");
          if (!c.last_name) missingFields.push("Last Name");
          if (!c.document_type) missingFields.push("Document Type");
          if (!c.document_number) missingFields.push("Document Number");
          if (!c.email) missingFields.push("Email");
          if (!c.phone) missingFields.push("Phone");
          if (!c.country) missingFields.push("Country");
          if (!c.city) missingFields.push("City");
          if (!c.address) missingFields.push("Address");
          if (!c.postal_code) missingFields.push("Postal Code");
          if (!c.gender) missingFields.push("Gender");
          if (!c.birth_date) missingFields.push("Birth Date");
          if (!firstIncomplete.is_main_guest && !firstIncomplete.relationship) {
              missingFields.push("Relationship to Main Guest");
          }
      
          const guestLabel = firstIncomplete.is_main_guest
              ? "Main Guest"
              : `Accompanying Guest ${firstIncompleteIndex + 1}`;
      
          alert(`⚠️ Missing information for ${guestLabel}:\n\n${missingFields.join(", ")}`);
          return;
      }
      
      
    
        try {
            // Crear nuevos clientes para los huéspedes que no tengan customer_id
            const updatedGuests = await Promise.all(guests.map(async (guest) => {
                if (!guest.customer.id) {
                    try {
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, guest.customer);
                        guest.customer.id = response.data.id; // Asignar el nuevo ID al huésped
                        console.log("Nuevo customer creado:", response.data);
                    } catch (error) {
                        console.error("Error al crear el nuevo customer:", error);
                        throw new Error("There was a problem creating a new customer.");
                    }

                
            } else {
                // Actualizar el `customer` existente si ya tiene ID
                try {
                    await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${guest.customer.id}`, guest.customer);
                    console.log(`Customer ${guest.customer.id} actualizado con éxito.`);
                } catch (error) {
                    console.error(`Error al actualizar el customer ${guest.customer.id}:`, error);
                    throw new Error(`There was a problem updating customer ${guest.customer.id}.`);
                }
            }
                
                return guest;
            }));

            // Ahora vinculamos los huéspedes con la reserva en `reservation_guests`
await Promise.all(updatedGuests.map(async (guest) => {
    console.log(`Intentando vincular guest_id: ${guest.customer.id} con reservation_id: ${id}`);
    console.log("Payload enviado a API:", {
        reservation_id: id,
        customer_id: guest.customer.id,
        is_main_guest: guest.is_main_guest,
        relationship: guest.is_main_guest ? null : guest.relationship
    });

    try {
        // Primero, verificar si el huésped ya está en reservation_guests
        const checkExisting = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservation-guests/reservation/${id}`);
        const existingGuest = checkExisting.data.find(g => g.customer_id === guest.customer.id);

        if (existingGuest) {
            console.log(`🔄 El huésped ${guest.customer.id} ya está en la reserva. Se actualizará.`);
            await axios.put(`${process.env.REACT_APP_API_URL}/api/reservation-guests/${existingGuest.id}`, {
                is_main_guest: guest.is_main_guest,
                relationship: guest.is_main_guest ? null : guest.relationship
            });
        } else {
            console.log(`➕ El huésped ${guest.customer.id} no está en la reserva. Se agregará.`);
            await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-guests`, {
                reservation_id: id,
                customer_id: guest.customer.id,
                is_main_guest: guest.is_main_guest,
                relationship: guest.is_main_guest ? null : guest.relationship
            });
        }

        console.log(`✅ Huésped ${guest.customer.id} vinculado a la reserva ${id}`);
    } catch (error) {
        console.error(`❌ Error al vincular el huésped ${guest.customer.id} con la reserva ${id}:`, error);
        throw new Error(`There was a problem linking guest ${guest.customer.id} to the reservation.`);
    }
}));

    
            // Obtener la fecha y hora actual
            const now = new Date();
            const arrivalDate = now.toISOString().split("T")[0]; // YYYY-MM-DD
            const arrivalTime = now.toTimeString().split(" ")[0]; // HH:MM:SS

            // Enviar la actualización de la reserva con los huéspedes y la fecha/hora de llegada
            await axios.put(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`, {
                status: "Checked In",
                guests: updatedGuests,
                arrival_date: arrivalDate,
                arrival_time: arrivalTime
            });

    
            // Obtener business_unit_id desde la reserva
            const businessUnitId = reservation.business_unit_id;

            // Redirigir con business_unit_id en la URL
            navigate(`/reservation/${id}?business_unit_id=${businessUnitId}`);

        } catch (error) {
            console.error("Error during check-in:", error);
            alert(error.message || "There was a problem completing the check-in.");
        }
    };

    const handleNoShow = async () => {
      if (!reservation?.id) return;

      try {
        setStatusActionLoading("no-show");
        await submitWaivedTerminalWorkflow({
          client: axios,
          apiBaseUrl: process.env.REACT_APP_API_URL,
          reservationId: reservation.id,
          workflow: "no_show",
        });

        const businessUnitId = reservation.business_unit_id;
        navigate(`/reservation/${reservation.id}?business_unit_id=${businessUnitId}`);
      } catch (error) {
        console.error("Error marking reservation as No Show:", error);
        alert(error?.response?.data?.error || "There was a problem marking the reservation as No Show.");
      } finally {
        setStatusActionLoading("");
        setShowNoShowDialog(false);
      }
    };
     
     
    if (loading) {
      return <div className="loading">Cargando...</div>;
    }

    const reservationPaymentBalance = findReservationBalance(paymentBalance, reservation?.id);
    const snapshotBaseCurrency = normalizeCurrencyCode(
      reservationPaymentBalance?.currency_snapshot?.base_currency_code
    );
    const paymentCurrency = normalizeCurrencyCode(
      currencyConfiguration
        ? displayCurrency
        : snapshotBaseCurrency || reservation?.base_currency_code || reservation?.currency || displayCurrency
    ) || "EUR";
    const paymentMinorUnits = getCurrencyMinorUnits(paymentCurrency, currencyCatalog);
    const totalPaidCents = reservationPaymentBalance
      ? moneyShapeToCents(reservationPaymentBalance.allocated_paid)
      : 0;
    const outstandingCents = reservationPaymentBalance
      ? moneyShapeToCents(reservationPaymentBalance.balance_due)
      : Math.max(moneyShapeToCents(reservation.total_price) - totalPaidCents, 0);
    const todayIso = new Date().toISOString().split("T")[0];
    const checkInDateIso = String(reservation?.check_in_date || "").slice(0, 10);
    const normalizedReservationStatus = String(reservation?.status || "").trim().toLowerCase();
    const canManageArrival = Boolean(checkInDateIso) && checkInDateIso <= todayIso;
    const canShowCheckInAction =
      canManageArrival &&
      normalizedReservationStatus !== "checked in" &&
      normalizedReservationStatus !== "checked out" &&
      normalizedReservationStatus !== "cancelled";
    const canShowNoShowAction =
      canManageArrival &&
      ["confirmed", "requested"].includes(normalizedReservationStatus);
    const formatReservationAmount = (baseAmount) => formatReservationBaseAmount({
      baseAmount,
      snapshot: reservationPaymentBalance?.currency_snapshot,
      targetCurrency: paymentCurrency,
      convertFromBase,
      currencyCatalog,
    });
     
     
         
    return (
      <div className="check-in-layout" style={{ display: "flex", height: "100vh" }}>
        {/* PANEL IZQUIERDO FIJO */}
        <div
          className="bg-dark text-light shadow"
          style={{
            width: "40%",
            height: "100vh",
            padding: "20px",
            position: "fixed",
            top: "60px", // ajusta según altura de tu navbar
            left: 0,
            overflowY: "auto",
            zIndex: 1000,
            borderRight: "2px solid #333"
          }}
        >
          <h4 className="text-warning fw-bold mb-3">Check-In for ID #{id}</h4>
          <p><strong>Check-in date:</strong> {reservation.check_in_date}</p>
          <p><strong>Check-out date:</strong> {reservation.check_out_date}</p>
          <p><strong>Check-in time:</strong> {reservation.check_in_time}</p>
          <p><strong>Check-out time:</strong> {reservation.check_out_time}</p>
          <p><strong>Adults:</strong> {reservation.adults}</p>
          <p><strong>Children:</strong> {reservation.children || 0}</p>
          <p><strong>Children Ages:</strong> {reservation.children_ages || 0}</p>
          <p><strong>Babies:</strong> {reservation.babies || 0}</p>
          <p><strong>Room Category:</strong> {reservation.room_category?.name || "N/A"}</p>
          <div className="d-flex align-items-center justify-content-between">
            <p className="mb-0">
              <strong>Room:</strong> {reservation.room ? `Room #${reservation.room.room_number}` : "No room assigned yet"}
            </p>
            {!editingRoom && (
              <button className="btn btn-sm btn-outline-warning ms-2" onClick={() => {
                fetchAvailableRooms();
                setEditingRoom(true);
              }}>
                Assign / Change Room
              </button>
            )}
          </div>

          {editingRoom && (
            <div className="mt-2 d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: "100%" }}
                value={selectedRoomId || ""}
                onChange={(e) => setSelectedRoomId(e.target.value)}
              >
                <option value="">Select a room</option>
                {availableRooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    Room #{room.room_number}
                  </option>
                ))}
              </select>

              <button
                className="btn btn-sm btn-success"
                onClick={handleAssignRoom}
                disabled={!selectedRoomId}
              >
                Confirm
              </button>

              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setEditingRoom(false);
                  setSelectedRoomId(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}


          <p><strong>Rate:</strong> {reservation.rate?.name || "N/A"}</p>
          <p><strong>Total Price:</strong> {reservation.total_price ? formatReservationAmount(reservation.total_price) : "N/A"}</p>
          <div className="mt-0">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="mb-0">
                <strong>Paid:</strong> {formatReservationAmount(totalPaidCents / 100)} / <strong>Outstanding:</strong> {formatReservationAmount(outstandingCents / 100)}
              </p>
              {!editingPayment && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setEditingPayment(true)}
                >
                  Pay
                </button>
              )}
            </div>
            
            {editingPayment && (
              <div className="d-flex align-items-center gap-2 mt-2">
                <input
                  type="number"
                  inputMode={paymentMinorUnits === 0 ? "numeric" : "decimal"}
                  step={currencyAmountStep(paymentCurrency, currencyCatalog)}
                  className="form-control form-control-sm"
                  placeholder={`Amount (${paymentCurrency})`}
                  value={newPayment?.amount_paid || ""}
                  onChange={(e) =>
                    setNewPayment((prev) => ({
                      ...prev,
                      amount_paid: e.target.value,
                    }))
                  }
                  style={{ maxWidth: "90px" }}
                />

                <select
                  className="form-select form-select-sm"
                  value={newPayment?.payment_method || "Cash"}
                  onChange={(e) =>
                    setNewPayment((prev) => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                  style={{ maxWidth: "120px" }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Other">Other</option>
                </select>

                <button
                  className="btn btn-sm btn-success"
                  disabled={!newPayment?.amount_paid}
                  onClick={async () => {
                    try {
                      const groupId = reservation?.reservation_group_id;
                      if (!groupId) {
                        alert("Reservation group not found for this reservation.");
                        return;
                      }

                      const preparedPayment = prepareCheckInManualPayment(
                        newPayment.amount_paid,
                        paymentCurrency,
                        currencyCatalog
                      );
                      if (!preparedPayment.valid) {
                        alert(preparedPayment.error);
                        return;
                      }

                      await axios.post(`${process.env.REACT_APP_API_URL}/api/stripe-transactions/groups/${groupId}/manual-payment`, {
                        amount: preparedPayment.amount,
                        currency: preparedPayment.currency,
                        method: normalizeManualPaymentMethod(newPayment.payment_method),
                        business_unit_id: reservation.business_unit_id,
                        allocation_mode: "manual",
                        allocations: [
                          {
                            reservation_id: reservation.id,
                            amount_cents: preparedPayment.amount_cents,
                          },
                        ],
                        note: `Manual payment from check-in reservation #${reservation.id}`,
                      });

                      const updated = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/reservations/${reservation.id}`
                      );
                      setReservation(updated.data);
                      fetchPaymentBalanceForReservation(updated.data)
                        .then((balance) => setPaymentBalance(balance))
                        .catch(() => setPaymentBalance(null));
                      setNewPayment({ amount_paid: "", payment_method: "Cash" });
                      setEditingPayment(false);
                    } catch (error) {
                      console.error("❌ Error registering payment:", error);
                      alert("Error registering payment.");
                    }
                  }}
                >
                  Confirm
                </button>

                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setEditingPayment(false);
                    setNewPayment({ amount_paid: "", payment_method: "Cash" });
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            
          </div>


          
          {reservation.special_requests && (
          <p><strong>Special Requests:</strong><br />{reservation.special_requests}</p>
          )}

    
          {(canShowCheckInAction || canShowNoShowAction) && (
            <div className="mt-4 d-flex flex-column gap-2">
              {canShowCheckInAction && (
                <button
                  className="btn fw-bold w-100"
                  style={{
                    backgroundColor: "#9b59b6",
                    color: "white",
                    border: "none",
                    transition: "background-color 0.3s",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#1abc9c")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "#9b59b6")}
                  onClick={handleCheckIn}
                  disabled={statusActionLoading === "no-show"}
                >
                  Check-In
                </button>
              )}

              {canShowNoShowAction && (
                <button
                  className="btn fw-bold w-100"
                  style={{
                    background: "linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)",
                    color: "white",
                    border: "none",
                  }}
                  onClick={() => setShowNoShowDialog(true)}
                  disabled={statusActionLoading === "no-show"}
                >
                  {statusActionLoading === "no-show" ? "Saving..." : "No Show"}
                </button>
              )}
            </div>
          )}
        </div>
    
        {/* PANEL DERECHO: tarjetas de huéspedes con scroll propio */}
        <div
          style={{
            position: "absolute", // 👈 Evita que esta sección empuje el body hacia abajo
            right: 0,
            top: "40px", // compensar navbar
            width: "60%",
            height: "calc(100vh - 60px)",
            overflowY: "auto",
            padding: "40px",
            backgroundColor: "#000"
          }}
        >
          {guests.map((guest, index) => (
            <div
            key={index}
            className="card bg-dark text-light p-2 mb-2"
            style={{
              fontSize: "0.85rem",
              zoom: "80%",              // ✅ Zoom real
              width: "fit-content",     // que se ajuste al contenido
              maxWidth: "100%",          // que no reviente en pantallas pequeñas
              marginLeft: "auto",       // alineado a la derecha como en ReservationDetails
              marginRight: "0"
            }}
          >
          
          
          <h4 className="text-warning fw-bold fs-5 text-center">
            {guest.is_main_guest
              ? "Main Guest"
              : index >= reservation.adults
                ? `Child ${index - reservation.adults + 1}`
                : `Accompanying Guest ${index}`}

          </h4>

    
              <div className="row">
              <CustomerForm
  formData={guest.customer}
  handleChange={(e) => {
    const updatedGuests = [...guests];
    const { name, value } = e.target;

    updatedGuests[index].customer = {
      ...updatedGuests[index].customer,
      [name]: value,
    };

    setGuests(updatedGuests);
  }}
  handleSelectChange={(selectedOption) => {
    const updatedGuests = [...guests];
    updatedGuests[index].customer.country = selectedOption ? selectedOption.value : "";
    setGuests(updatedGuests);
  }}
  showAllRequired={true}
  index={index}  // Añadir index aquí
  onBlur={(e) => {
    const value = e.target.value;
    const isChild = index >= reservation.adults; // Verifica si el huésped es un niño

    if (e.target.name === "birth_date") {
      handleAgeValidation(index, value); // Llama a la validación de edad
    }
  }}
/>









              {!guest.is_main_guest && (
                <div className="mt-2">
                  <label className="form-label">Relationship to Main Guest {<span className="text-danger fw-bold">*</span>} </label>
                  <input
                    type="text"
                    className="form-control form-control-sm p-2"
                    value={guest.relationship || ""}
                    onChange={(e) => {
                      const updatedGuests = [...guests];
                      updatedGuests[index].relationship = e.target.value;
                      setGuests(updatedGuests);
                    }}
                  />
                </div>
              )}

                    </div>
                  </div>
                ))}
              </div>
        <PmsConfirmDialog
          isOpen={showNoShowDialog}
          eyebrow="Front Desk"
          title="Mark reservation as No Show?"
          message={`This will update reservation #${reservation?.id || id} to No Show and close the arrival flow for this guest. No no-show charge will be applied.`}
          confirmLabel={statusActionLoading === "no-show" ? "Saving..." : "No Show"}
          cancelLabel="Back"
          onClose={() => setShowNoShowDialog(false)}
          onConfirm={() => {
            void handleNoShow();
          }}
          isBusy={statusActionLoading === "no-show"}
          tone="warning"
        />
      </div>
    );
            
   
}

export default CheckIn;
